import { yupResolver } from '@hookform/resolvers/yup';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import { Button } from '@openx/components-core/lib/Button/Button';
import { DialogTitle } from '@openx/components-core/lib/Dialog/DialogTitle/DialogTitle';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDirectDealsContext } from '../../utils/context/DirectDealsContext';
import { useRequestDirectDealModalContext } from '../../utils/context/RequestDirectDealModalContext';
import { defaultValues } from './form/const';
import type { RequestDirectDealForm } from './form/types';
import { transformFormData } from './form/utils';
import { getValidationSchema } from './form/validationSchema';
import { ModalContent } from './ModalContent';

export const RequestDirectDealModal = () => {
  const { t } = useTranslation();

  const {
    isOpen,
    data: { publisherId, publisherName, callback },
    closeModal,
  } = useRequestDirectDealModalContext();
  const { useDirectDealsContact } = useDirectDealsContext();
  const { requestContact } = useDirectDealsContact();

  const formMethods = useForm<RequestDirectDealForm>({
    defaultValues,
    resolver: yupResolver(getValidationSchema(t)),
    mode: 'all',
  });

  const onFormSubmit = async (formData: RequestDirectDealForm) => {
    await requestContact({ publisherId, publisherName, data: transformFormData(formData), callback });
    onModalClose();
  };

  const onModalClose = () => {
    formMethods.reset();
    closeModal();
  };

  return (
    <FormProvider {...formMethods}>
      <Dialog open={isOpen} onClose={onModalClose} data-test="request-direct-deal-modal">
        <DialogTitle onClose={onModalClose} dataTest="request-direct-deal-modal-title">
          {t('Request a Direct Deal')}
        </DialogTitle>

        <ModalContent publisherName={publisherName} />

        <DialogActions>
          <Button onClick={onModalClose} variant="text">
            {t('Cancel')}
          </Button>

          <Button
            data-test="submit-request"
            color="primary"
            loading={formMethods.formState.isSubmitting}
            onClick={formMethods.handleSubmit(onFormSubmit)}
          >
            {t('Submit request')}
          </Button>
        </DialogActions>
      </Dialog>
    </FormProvider>
  );
};
